import React from 'react';
import { Factory, Target, Lightbulb, Users, CheckCircle2, Quote } from 'lucide-react';
import { aboutContent, companyInfo, managingDirector } from '../mock';

const AboutPage = () => {
  return (
    <div data-testid="about-page" className="bg-white pt-24 sm:pt-28 pb-16 sm:pb-24">
      {/* Page Header */}
      <section className="py-10 sm:py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 text-center">
          <p className="font-rajdhani text-brand font-semibold text-xs sm:text-sm tracking-[0.25em] uppercase mb-2 sm:mb-3">
            About Us
          </p>
          <h1 className="font-rajdhani text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-800">
            About SynergyTuff
          </h1>
          <p className="font-roboto text-slate-500 mt-3 sm:mt-4 text-sm sm:text-base max-w-xl mx-auto">
            Five decades of glass manufacturing excellence
          </p>
        </div>
      </section>

      {/* Legacy Section */}
      <section className="py-12 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid md:grid-cols-2 gap-8 sm:gap-16 items-center">
            <div className="space-y-4 sm:space-y-6">
              <p className="font-rajdhani text-brand font-semibold text-xs sm:text-sm tracking-[0.25em] uppercase">
                Our Legacy
              </p>
              <h2 className="font-rajdhani text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-800 leading-tight">
                Built on {companyInfo.experience} of Industry Expertise
              </h2>
              <p className="font-roboto text-slate-600 leading-relaxed text-sm sm:text-base">
                {aboutContent.legacy}
              </p>
            </div>
            <div className="relative h-64 sm:h-[420px] overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1654105727849-1b9d39357f16"
                alt="Manufacturing Excellence"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Excellence Section */}
      <section className="py-12 sm:py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid md:grid-cols-2 gap-8 sm:gap-16 items-center">
            <div className="relative h-64 sm:h-[420px] overflow-hidden order-2 md:order-1">
              <img
                src="https://images.unsplash.com/photo-1677253200111-97d0d490d60e"
                alt="Jaipur Manufacturing"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="space-y-4 sm:space-y-6 order-1 md:order-2">
              <p className="font-rajdhani text-brand font-semibold text-xs sm:text-sm tracking-[0.25em] uppercase">
                Manufacturing Excellence
              </p>
              <h2 className="font-rajdhani text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-800 leading-tight">
                State-of-the-Art Facility in Jaipur
              </h2>
              <p className="font-roboto text-slate-600 leading-relaxed text-sm sm:text-base">
                {aboutContent.excellence}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Innovation Section */}
      <section className="py-12 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid md:grid-cols-2 gap-8 sm:gap-16 items-start">
            <div className="space-y-4 sm:space-y-6">
              <p className="font-rajdhani text-brand font-semibold text-xs sm:text-sm tracking-[0.25em] uppercase">
                Innovation & Technology
              </p>
              <h2 className="font-rajdhani text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-800 leading-tight">
                Driving the Future of Glass Solutions
              </h2>
              <p className="font-roboto text-slate-600 leading-relaxed text-sm sm:text-base">
                {aboutContent.innovation}
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3 sm:gap-5">
              {[
                { icon: Factory, title: "Advanced Manufacturing", desc: "Computer-controlled precision" },
                { icon: Target, title: "Quality Focus", desc: "Zero-compromise standards" },
                { icon: Lightbulb, title: "R&D Investment", desc: "Continuous innovation" },
                { icon: Users, title: "Skilled Team", desc: "Expert craftsmanship" }
              ].map((item, index) => (
                <div key={index} className="p-4 sm:p-6 border border-slate-200 hover:border-brand/30 hover:shadow-sm transition-all group">
                  <item.icon size={24} className="text-brand mb-2 sm:mb-3 group-hover:scale-110 transition-transform" />
                  <h4 className="font-rajdhani text-slate-800 font-semibold text-xs sm:text-sm mb-1">{item.title}</h4>
                  <p className="font-roboto text-slate-500 text-[10px] sm:text-xs">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Managing Director Section */}
      <section className="py-12 sm:py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-8 sm:mb-14">
            <p className="font-rajdhani text-brand font-semibold text-xs sm:text-sm tracking-[0.25em] uppercase mb-2 sm:mb-3">
              Leadership
            </p>
            <h2 className="font-rajdhani text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-800">
              {managingDirector.headline}
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8 sm:gap-10 items-start">
            {/* MD Photo */}
            <div data-testid="md-profile" className="md:col-span-1">
              <div className="overflow-hidden border-4 border-brand/20 max-w-xs mx-auto md:max-w-none">
                <img
                  src={managingDirector.photo}
                  alt={managingDirector.name}
                  className="w-full aspect-[3/4] object-cover"
                />
              </div>
              <div className="mt-4 sm:mt-5 text-center">
                <h3 className="font-rajdhani text-xl sm:text-2xl font-bold text-slate-800">
                  {managingDirector.name}
                </h3>
                <p className="font-roboto text-brand font-medium text-sm">
                  {managingDirector.title}
                </p>
                <p className="font-roboto text-slate-500 text-xs mt-1">
                  SynergyTuff India Private Limited
                </p>
              </div>
            </div>

            {/* MD Bio */}
            <div className="md:col-span-2 space-y-4 sm:space-y-5">
              {managingDirector.bio.split('\n\n').map((paragraph, index) => (
                <p key={index} className="font-roboto text-slate-600 leading-relaxed text-sm sm:text-base">
                  {paragraph}
                </p>
              ))}

              {/* Quote */}
              <div className="relative p-5 sm:p-8 bg-white border-l-4 border-brand mt-6 sm:mt-8">
                <Quote size={28} className="text-brand/20 absolute top-3 right-3 sm:top-4 sm:right-4" />
                <p className="font-roboto text-slate-700 text-sm sm:text-base italic leading-relaxed mb-3">
                  "{managingDirector.quote}"
                </p>
                <p className="font-rajdhani text-brand font-semibold text-sm">
                  -- {managingDirector.name}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-12 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-8 sm:mb-14">
            <p className="font-rajdhani text-brand font-semibold text-xs sm:text-sm tracking-[0.25em] uppercase mb-2 sm:mb-3">
              Our Principles
            </p>
            <h2 className="font-rajdhani text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-800">
              Core Values
            </h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-5">
            {aboutContent.values.map((value, index) => (
              <div
                key={index}
                className="p-4 sm:p-6 text-center border border-slate-200 hover:border-brand/30 hover:shadow-sm transition-all group"
              >
                <CheckCircle2 size={24} className="text-brand mx-auto mb-2 sm:mb-3 group-hover:scale-110 transition-transform" />
                <p className="font-rajdhani text-slate-800 font-semibold text-xs sm:text-sm">{value}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
